import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { UserRound, Loader2, ShieldCheck } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { useAuth } from "@/auth/AuthContext";
import { Input } from "@/components/ui/input";
import { Honeypot, ChallengeField, useChallenge, securityDetail } from "@/components/Security";

export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [hp, setHp] = useState("");
  const { challenge, answer, setAnswer, refresh, payload } = useChallenge();

  useEffect(() => {
    if (mode === "register") refresh();  // human verification is part of registration
  }, [mode, refresh]);

  const submitForgot = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setForgotSent(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (mode === "register") {
        await register(name, email, password, { hp, ...payload() });
      } else {
        const data = await login(email, password);
        if (data?.role === "vendor") navigate("/vendor");
      }
    } catch (err) {
      const sec = securityDetail(err);
      const detail = err.response?.data?.detail;
      if (sec?.code === "challenge_required") refresh();  // fresh question after a failed/expired attempt
      setError(sec?.message || (typeof detail === "string" ? detail : "Something went wrong. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="auth-page" className="mx-auto max-w-md px-6 pt-24 pb-40 lg:pt-32">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="space-y-4"
      >
        <div className="border border-[rgba(200,169,106,0.35)] bg-paper px-8 pt-8 pb-6 text-center space-y-3 relative overflow-hidden" data-testid="portal-header">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(200,169,106,0.12),transparent_70%)] pointer-events-none" aria-hidden="true" />
          <span className="inline-block font-stencil text-xl leading-none text-[#0B101C] bg-clinical px-3 py-2 rounded-lg shadow-[0_0_22px_rgba(200,169,106,0.3)] relative">VF</span>
          <h1 className="font-serif text-2xl text-ink tracking-tight relative">VetForge Customer Portal</h1>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#8E96AB] relative">Verified researchers 21+ only</p>
        </div>
        {forgot ? (
          <form onSubmit={submitForgot} className="border border-[#232B40] bg-paper p-10 space-y-8" data-testid="forgot-form">
            <div className="space-y-3 text-center">
              <UserRound className="mx-auto h-8 w-8 text-clinical" strokeWidth={1.5} />
              <h1 className="font-serif text-3xl text-ink">Reset password</h1>
              <p className="text-xs uppercase tracking-[0.25em] text-[#8E96AB]">We email you a reset link</p>
            </div>
            {forgotSent ? (
              <p className="text-xs text-clinical border border-clinical/30 bg-clinical/5 px-4 py-3 leading-relaxed" data-testid="forgot-success">
                If an account exists with this email, a reset link has been sent. The link is valid for one hour.
              </p>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]" htmlFor="forgot-email">Email</label>
                  <Input id="forgot-email" type="email" data-testid="forgot-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-12" />
                </div>
                {error && <p className="text-xs text-[#EF4444] border border-[#EF4444]/30 bg-[#EF4444]/5 px-4 py-3" data-testid="forgot-error">{error}</p>}
                <button
                  type="submit"
                  data-testid="forgot-submit-button"
                  disabled={submitting}
                  className="w-full bg-clinical text-[#0B101C] text-xs uppercase tracking-[0.2em] py-4 hover:bg-[#DBC28C] transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send reset link
                </button>
              </>
            )}
            <button
              type="button"
              data-testid="forgot-back-button"
              onClick={() => { setForgot(false); setForgotSent(false); setError(""); }}
              className="w-full text-xs uppercase tracking-[0.2em] text-[#8E96AB] hover:text-ink transition-colors duration-300"
            >
              Back to sign in
            </button>
          </form>
        ) : (
        <form onSubmit={submit} className="border border-[#232B40] bg-paper p-10 space-y-8" data-testid="auth-form">
          <div className="space-y-3 text-center">
            <UserRound className="mx-auto h-8 w-8 text-clinical" strokeWidth={1.5} />
            <h1 className="font-serif text-3xl text-ink">
              {mode === "register" ? "Create your account" : "Welcome back"}
            </h1>
            <p className="text-xs uppercase tracking-[0.25em] text-[#8E96AB]">
              Account required to browse and order
            </p>
          </div>

          <div className="grid grid-cols-2 gap-px bg-[#232B40] border border-[#232B40]">
            {["register", "login"].map((m) => (
              <button
                type="button"
                key={m}
                data-testid={`auth-mode-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className={`text-xs uppercase tracking-[0.2em] py-3 transition-colors duration-300 ${
                  mode === m ? "bg-clinical text-[#0B101C]" : "bg-paper text-[#8E96AB] hover:text-ink"
                }`}
              >
                {m === "register" ? "Create account" : "Sign in"}
              </button>
            ))}
          </div>

          {mode === "register" && (
            <div className="space-y-2">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]" htmlFor="auth-name">Full name *</label>
              <Input id="auth-name" data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-12" />
            </div>
          )}
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]" htmlFor="auth-email">Email *</label>
            <Input id="auth-email" type="email" data-testid="auth-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-12" />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#8E96AB]" htmlFor="auth-password">
              Password *{mode === "register" && <span className="normal-case tracking-normal"> (min 8 characters)</span>}
            </label>
            <Input id="auth-password" type="password" data-testid="auth-password-input" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none border-[rgba(200,169,106,0.28)] bg-transparent focus-visible:ring-clinical h-12" />
          </div>

          {mode === "register" && (
            <>
              <Honeypot value={hp} onChange={setHp} testid="auth-honeypot" />
              <ChallengeField challenge={challenge} answer={answer} setAnswer={setAnswer} onRefresh={refresh} testid="auth-challenge" />
            </>
          )}

          {error && (
            <p className="text-xs text-[#EF4444] border border-[#EF4444]/30 bg-[#EF4444]/5 px-4 py-3" data-testid="auth-error">{error}</p>
          )}

          <button
            type="submit"
            data-testid="auth-submit-button"
            disabled={submitting}
            className="w-full bg-clinical text-[#0B101C] text-xs uppercase tracking-[0.2em] py-4 hover:bg-[#DBC28C] transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === "register" ? "Create account" : "Sign in"}
          </button>

          {mode === "login" && (
            <button
              type="button"
              data-testid="forgot-password-link"
              onClick={() => setForgot(true)}
              className="w-full text-xs uppercase tracking-[0.2em] text-[#8E96AB] hover:text-clinical transition-colors duration-300"
            >
              Forgot password?
            </button>
          )}

          <p className="text-[10px] uppercase tracking-[0.2em] text-warning text-center leading-loose">
            Research use only — Not for human consumption
          </p>
        </form>
        )}
        <div className="border border-[#232B40] bg-paper px-5 py-4 flex gap-3" data-testid="anti-phishing-notice">
          <ShieldCheck className="h-5 w-5 text-clinical shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-[11px] text-[#B9BFCE] leading-relaxed">
            <span className="text-ink font-semibold">Security notice.</span> Our only official websites are{" "}
            <span className="text-clinical">vetforgehealth.com</span> and{" "}
            <span className="text-clinical">vetforge-pharma.emergent.host</span> — always check the address bar.
            Beware of look-alike sites: never enter your details or send payment anywhere else.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
